import { Link } from 'react-router-dom';
import { ArrowRight, Stethoscope, HeartPulse, Shield, Microscope, Activity, UserCheck, Scissors, Bandage, CheckCircle, Calendar, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const services = [
  {
    id: 'consultations',
    icon: Stethoscope,
    title: 'General Consultations',
    description: 'Comprehensive medical evaluations with our experienced physicians. We take the time to listen, examine, and understand your health concerns to deliver accurate care.',
    features: [
      'In-depth medical history review',
      'Comprehensive physical examinations',
      'Second opinion consultations',
      'Follow-up appointments and monitoring',
    ],
    featured: true,
  },
  {
    id: 'assessments',
    icon: Activity,
    title: 'Health Assessments',
    description: 'Thorough preventive health screenings and wellness assessments designed to identify risks early and keep you at your best.',
    features: [
      'Annual physical examinations',
      'Executive health screenings',
      'Chronic disease risk assessments',
      'Lifestyle and wellness evaluations',
    ],
  },
  {
    id: 'diagnosis',
    icon: Microscope,
    title: 'Diagnosis & Treatment',
    description: 'Accurate diagnosis using advanced diagnostic techniques, followed by evidence-based treatment plans tailored to your condition.',
    features: [
      'Laboratory testing and interpretation',
      'Diagnostic imaging coordination',
      'Chronic illness management',
      'Acute illness treatment',
    ],
  },
  {
    id: 'preventive',
    icon: Shield,
    title: 'Preventive Care',
    description: 'Proactive healthcare services focused on preventing illness, promoting wellness, and maintaining long-term health.',
    features: [
      'Immunizations and vaccinations',
      'Cancer screenings',
      'Cardiovascular risk assessment',
      'Nutrition and lifestyle counseling',
    ],
  },
  {
    id: 'surgical',
    icon: Scissors,
    title: 'Minor Surgical Procedures',
    description: 'Safe, in-office surgical procedures performed by skilled surgeons using modern techniques for minimal discomfort and faster recovery.',
    features: [
      'Skin lesion and mole removal',
      'Minor laceration repair',
      'Biopsies and excisions',
      'Cyst and abscess procedures',
    ],
  },
  {
    id: 'wound',
    icon: Bandage,
    title: 'Wound Management',
    description: 'Expert wound care services from acute injuries to chronic wound healing, with specialized techniques to promote optimal recovery.',
    features: [
      'Acute wound treatment',
      'Chronic wound care',
      'Dressing changes and monitoring',
      'Infection prevention and management',
    ],
  },
  {
    id: 'postop',
    icon: UserCheck,
    title: 'Postoperative Care',
    description: 'Comprehensive follow-up care to ensure smooth recovery after surgical procedures, with ongoing support and monitoring.',
    features: [
      'Recovery assessment and monitoring',
      'Pain management',
      'Suture and staple removal',
      'Rehabilitation guidance',
    ],
  },
  {
    id: 'education',
    icon: HeartPulse,
    title: 'Patient Education & Support',
    description: 'We empower you with knowledge about your health, treatment options, and preventive strategies for lasting wellness.',
    features: [
      'Treatment plan education',
      'Medication counseling',
      'Lifestyle modification guidance',
      'Family and caregiver support',
    ],
  },
];

const faqs = [
  {
    question: 'Do I need an appointment or can I walk in?',
    answer: 'We recommend scheduling an appointment to minimize wait times and ensure dedicated time with your physician. Call us at (855) 831-1992 or use our contact form. Urgent walk-ins are accommodated when possible.',
  },
  {
    question: 'What insurance plans do you accept?',
    answer: 'We accept most major insurance plans. Please contact our office with your insurance information so we can verify coverage before your visit.',
  },
  {
    question: 'What should I bring to my first appointment?',
    answer: 'Please bring a valid photo ID, your insurance card, a list of current medications, any relevant medical records, and your medical history. Arriving 15 minutes early helps us complete new patient paperwork.',
  },
  {
    question: 'Are your surgical procedures performed in-office?',
    answer: 'Yes, our minor surgical procedures are safely performed in our fully equipped treatment rooms by board-certified surgeons. For more complex procedures requiring hospital settings, we provide coordinated referrals.',
  },
  {
    question: 'How quickly can I be seen for an urgent concern?',
    answer: 'We make every effort to accommodate urgent medical needs same-day. Please call us immediately at (855) 831-1992 and our team will schedule you as soon as possible.',
  },
];

export const Services = () => {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="relative py-24 md:py-32 bg-navy" data-testid="services-hero">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=1920&q=80"
            alt="Medical professionals"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-emerald text-white font-semibold text-xs uppercase tracking-wider mb-6">
              Our Services
            </span>
            <h1 className="font-manrope font-bold text-4xl sm:text-5xl text-white leading-tight mb-6">
              Complete Medical & Surgical Care
            </h1>
            <p className="text-lg text-navy-200 leading-relaxed">
              From routine consultations to specialized surgical procedures, our comprehensive services are designed to meet all your healthcare needs under one trusted roof.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white" data-testid="services-grid">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <Card 
                key={service.id}
                className="bg-white border-silver-light shadow-card card-hover group"
                data-testid={`service-tile-${service.id}`}
              >
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-sm flex items-center justify-center mb-4 ${
                    service.featured ? 'bg-emerald' : 'bg-navy-50 group-hover:bg-emerald'
                  } transition-colors`}>
                    <service.icon className={`w-6 h-6 ${
                      service.featured ? 'text-white' : 'text-navy group-hover:text-white'
                    } transition-colors`} />
                  </div>
                  <h3 className="font-manrope font-semibold text-navy text-lg mb-2">{service.title}</h3>
                  <p className="text-sm text-navy-600">{service.description.substring(0, 90)}...</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="section-padding bg-navy-50" data-testid="services-details">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
              What We Offer
            </span>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy">
              Service Details
            </h2>
          </div>

          <div className="space-y-16">
            {services.slice(0, 4).map((service, index) => (
              <div 
                key={service.id}
                className={`grid lg:grid-cols-2 gap-12 items-center`}
                data-testid={`service-detail-${service.id}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-sm bg-emerald flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="font-manrope font-bold text-2xl md:text-3xl text-navy">{service.title}</h2>
                  </div>
                  <p className="text-navy-600 leading-relaxed mb-6">{service.description}</p>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-emerald flex-shrink-0 mt-0.5" />
                        <span className="text-navy-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    asChild
                    className="bg-emerald hover:bg-emerald-600 text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
                    data-testid={`service-${service.id}-cta`}
                  >
                    <Link to="/contact">
                      <Calendar className="mr-2 w-4 h-4" />
                      Book Appointment
                    </Link>
                  </Button>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="bg-white border border-silver-light shadow-card p-8">
                    <h3 className="font-manrope font-semibold text-navy text-lg mb-4">Why This Matters</h3>
                    <p className="text-sm text-navy-600 mb-6">
                      Our approach combines medical expertise with compassionate care, ensuring you receive treatment that improves your health and quality of life.
                    </p>
                    <div className="bg-navy p-6">
                      <div className="flex items-center gap-3 text-white">
                        <Phone className="w-5 h-5 text-emerald" />
                        <div>
                          <div className="text-xs text-navy-200 uppercase tracking-wider">Questions?</div>
                          <a href="tel:+18558311992" className="font-manrope font-semibold hover:text-emerald transition-colors">
                            (855) 831-1992
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services (last 4) */}
      <section className="section-padding bg-white" data-testid="additional-services">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
              Procedures & Recovery
            </span>
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy">
              Surgical & Recovery Services
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.slice(4).map((service) => (
              <Card 
                key={service.id}
                className="bg-white border-silver-light shadow-card"
                data-testid={`procedure-${service.id}`}
              >
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-sm bg-emerald-50 flex items-center justify-center">
                      <service.icon className="w-5 h-5 text-emerald" />
                    </div>
                    <h3 className="font-manrope font-semibold text-navy text-xl">{service.title}</h3>
                  </div>
                  <p className="text-navy-600 mb-5">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald flex-shrink-0 mt-0.5" />
                        <span className="text-navy-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-padding bg-navy-50" data-testid="services-faq">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block text-emerald font-semibold text-xs uppercase tracking-wider mb-3">
                FAQs
              </span>
              <h2 className="font-manrope font-bold text-3xl md:text-4xl text-navy">
                Patient Questions
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`}
                  className="bg-white border-none px-6"
                  data-testid={`faq-${index}`}
                >
                  <AccordionTrigger className="text-left font-manrope font-semibold text-navy hover:text-emerald hover:no-underline py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-navy-600 pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-emerald" data-testid="services-cta">
        <div className="container-custom text-center">
          <h2 className="font-manrope font-bold text-3xl md:text-4xl text-white mb-4">
            Ready to Schedule Your Visit?
          </h2>
          <p className="text-emerald-100 text-lg mb-8 max-w-2xl mx-auto">
            Our team is here to provide the care you deserve. Contact us today to book your appointment.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              asChild
              className="bg-white text-emerald hover:bg-navy hover:text-white rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8"
              data-testid="services-cta-book-btn"
            >
              <Link to="/contact">
                <Calendar className="mr-2 w-4 h-4" />
                Book Online
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-emerald rounded-none font-semibold tracking-wide uppercase text-xs h-12 px-8 bg-transparent"
              data-testid="services-cta-call-btn"
            >
              <a href="tel:+18558311992">
                <Phone className="mr-2 w-4 h-4" />
                (855) 831-1992
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
